import { Stack, Typography, styled } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { background, black, text } from "@/modules/irohana-components/theme/colors-new";

export type TSegmentedControlOption<T extends string = string> = {
  label: string;
  value: T;
  disabled?: boolean;
};

type TSegmentedControlProps<T extends string = string> = {
  value: T;
  options: TSegmentedControlOption<T>[];
  onChange: (value: T) => void;
};

export const SegmentedControl = <T extends string = string>({ value, options, onChange }: TSegmentedControlProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [markerStyle, setMarkerStyle] = useState({ width: 0, left: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeIndex = options.findIndex((o) => o.value === value);
    const activeEl = container.children[activeIndex + 1] as HTMLElement | undefined;
    if (activeEl) {
      setMarkerStyle({ width: activeEl.offsetWidth, left: activeEl.offsetLeft });
    }
  }, [value, options]);

  return (
    <Container ref={containerRef}>
      <Marker style={{ width: markerStyle.width, left: markerStyle.left }} />
      {options.map((option) => (
        <Stack
          key={option.value}
          onClick={() => !option.disabled && onChange(option.value)}
          sx={{ cursor: option.disabled ? "not-allowed" : "pointer", zIndex: 1, opacity: option.disabled ? 0.4 : 1 }}
        >
          <Typography variant="body14" padding="8px 16px" color={value === option.value ? black[30] : text.secondary}>
            {option.label}
          </Typography>
        </Stack>
      ))}
    </Container>
  );
};

const Container = styled(Stack)`
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  background-color: ${background.disable};
  border-radius: 24px;
  padding: 4px;
  width: fit-content;
`;

const Marker = styled(Stack)`
  position: absolute;
  top: 50%;
  left: 4px;
  transform: translateY(-50%);
  height: calc(100% - 8px);
  width: calc(50% - 4px);
  transition: all 0.3s ease;
  z-index: 0;
  background-color: ${background.white};
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;
